import { useEffect, useRef, useState } from "react"
import classNames from "classnames"
import { debounce } from "lodash"

import { ReactComponent as IconMinus } from "assets/svg/icon-minus.svg"
import { ReactComponent as IconPlus } from "assets/svg/icon-plus.svg"

import styles from "./index.module.scss"

interface Props {
  value: number
  disabled?: boolean
  onInputChange: (value: number) => void
  minValue?: number
  maxValue?: number
  dataTestId?: string
}

export const InputNumber = ({ onInputChange, value, disabled, minValue, maxValue, dataTestId }: Props) => {
  const [permanentValue, setPermanentValue] = useState(value)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const disabledMinValue = (minValue || minValue === 0) && value <= minValue
  const disabledMaxValue = (maxValue || maxValue === 0) && value >= maxValue

  useEffect(() => {
    if (value !== permanentValue) {
      setPermanentValue(value)
    }
  }, [value])

  useEffect(() => {
    if (inputRef.current && !disabled) {
      inputRef.current.focus()
    }
  }, [inputRef.current, disabled])

  const onQuantityChange = (value: number) => {
    onInputChange(value)
    setPermanentValue(value)
  }

  const setDebounceQuantityChange = useRef<typeof onQuantityChange | null>(null)

  useEffect(() => {
    setDebounceQuantityChange.current = debounce(onQuantityChange, 200)
  }, [onInputChange])

  const calculateValue = (value: number) => {
    if ((minValue || minValue === 0) && value < minValue) {
      return minValue
    }

    if (maxValue && value > maxValue) {
      return maxValue
    }

    return value
  }

  const handleInputChange = (value: number) => {
    const calculatedValue = calculateValue(value)
    setDebounceQuantityChange.current?.(calculatedValue)
    setPermanentValue(value)
  }

  const handleButtonClick = (e: React.MouseEvent, newValue: number) => {
    e.preventDefault()
    onInputChange(newValue)
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
  }

  return (
    <div className={styles.inputNumberWrapper}>
      <span
        className={classNames(styles.inputNumberButton, {
          [styles.disabled]: (minValue || minValue === 0) && value <= minValue,
        })}
        onClick={e => !disabledMinValue && handleButtonClick(e, value - 1)}
        onMouseDown={handleMouseDown}
        data-testid={dataTestId ? `${dataTestId}-decrement` : undefined}
        aria-disabled={!!disabledMinValue}
      >
        <IconMinus />
      </span>
      <input
        value={String(permanentValue)}
        ref={inputRef}
        type="number"
        className={styles.inputNumber}
        onChange={event => handleInputChange(Number(event?.currentTarget?.value))}
        data-testid={dataTestId ? `${dataTestId}-input` : undefined}
      />
      <span
        className={classNames(styles.inputNumberButton, {
          [styles.disabled]: maxValue && value >= maxValue,
        })}
        onClick={e => !disabledMaxValue && handleButtonClick(e, value + 1)}
        onMouseDown={handleMouseDown}
        data-testid={dataTestId ? `${dataTestId}-increment` : undefined}
        aria-disabled={!!disabledMaxValue}
      >
        <IconPlus />
      </span>
    </div>
  )
}
